import type { GeneratedApplicationProposal } from "@cogo/a2a-gateway/generated-application-proposal";
import { stableStringify } from "@cogo/publisher-core/content-shape";
import type { ContentHash, ContentRef } from "@cogo/publisher-core";
import { hashCanonicalContent as hashCanonicalContentSync } from "@cogo/schema/canonical-content-hash";

import {
  createProject,
  listProjects,
  loadProject,
  patchProjectSettings,
  persistGeneratedBlocks,
  type PersistGeneratedBlocksRevisionResult,
  type CreateProjectTemplate,
  type StudioProject,
  type StudioProjectSettingsRevisionRef,
  type StudioProjectServerRevisionRef,
} from "@/services/project-api";

import {
  ARTIFACT_PERSISTENCE_RECEIPT_SCHEMA_VERSION,
  type ArtifactPersistenceReceipt,
  type ArtifactRevisionRef,
  type CommittedArtifactRevisionEnvelope,
} from "./artifact-revision-envelope";
import {
  GENERATED_ARTIFACT_PERSISTENCE_PORT_KIND,
  type GeneratedArtifactPersistenceCommand,
  type GeneratedArtifactPersistencePort,
} from "./commit-generated-artifact";

export const GENERATED_ARTIFACT_PERSISTENCE_SETTINGS_SCHEMA_VERSION =
  "cogo.generated-artifact-persistence-settings.v3" as const;

export const STUDIO_PROJECT_SERVER_REVISION_AUTHORITY =
  "studio-project-server" as const;

/** Machine-readable capability statement for A4 caller/release gates. */
export const GENERATED_ARTIFACT_PERSISTENCE_SERVER_CAPABILITIES = {
  projectCas: "block-bound-composite-revision",
  botProjectIdempotency: "server-binding-reuse-client-coordinated",
  botRevisionOwnership: "explicit-existing-bot-project-target",
  durableReceiptLedger: "settings-v3-approval-bound-project-revision",
  atomicCrossClientGuarantee: false,
} as const;

export interface StoredGeneratedArtifactCommit {
  artifactId: string;
  requestId: string;
  runId: string;
  approvalDecisionId: string;
  approvalActorId: string;
  approvalActorKind: "human";
  contentHash: ContentHash;
  blockName: string;
  canonicalCogoJson: Record<string, unknown>;
  parentRevision: ArtifactRevisionRef;
  /** Revision of this exact approved artifact, not the next project observation. */
  committedArtifactRevision: ArtifactRevisionRef;
  /** Exact block-bound project revision issued by the Studio authority. */
  projectRevision: StudioProjectServerRevisionRef;
  /** Durable write/readback proof used to reconstruct identity after reload. */
  persistenceReceipt: ArtifactPersistenceReceipt;
  evidenceRefs: readonly ContentRef[];
  recordedAt: string;
}

export interface GeneratedArtifactBirthAuthority {
  approvalDecisionId: string;
  approvalActorId: string;
  approvalActorKind: "human";
  botProfileParentRevision: {
    authority: "studio-bot-profile-updated-at";
    value: string;
  };
}

export interface GeneratedArtifactPersistenceSettings {
  schemaVersion: typeof GENERATED_ARTIFACT_PERSISTENCE_SETTINGS_SCHEMA_VERSION;
  /**
   * Deterministic retry identity for a Bot-owned project. `null` means this
   * binding belongs to an existing Chat project instead of a Bot create.
   */
  creationKey: ContentHash | null;
  /** Server-read owner binding for projects born from Bot. */
  ownerBotProfileId: string | null;
  birthAuthority: GeneratedArtifactBirthAuthority | null;
  commits: StoredGeneratedArtifactCommit[];
}

type LoadedStudioProjectReadback = Awaited<ReturnType<typeof loadProject>>;

export class GeneratedArtifactProjectConflictError extends Error {
  readonly code = "GENERATED_ARTIFACT_PROJECT_CONFLICT" as const;

  constructor(message = "The target project changed before artifact persistence.") {
    super(message);
    this.name = "GeneratedArtifactProjectConflictError";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isContentHash(value: unknown): value is ContentHash {
  return typeof value === "string" && /^sha256:[a-f0-9]{64}$/.test(value);
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

function readRevisionRef(value: unknown): ArtifactRevisionRef | null {
  if (!isRecord(value)) return null;
  const authority = nonEmptyString(value.authority);
  const revisionValue = nonEmptyString(value.value);
  return authority && revisionValue
    ? { authority, value: revisionValue }
    : null;
}

function readProjectRevisionRef(
  value: unknown,
): StudioProjectServerRevisionRef | null {
  const revision = readRevisionRef(value);
  return revision?.authority === STUDIO_PROJECT_SERVER_REVISION_AUTHORITY
    ? {
        authority: STUDIO_PROJECT_SERVER_REVISION_AUTHORITY,
        value: revision.value,
      }
    : null;
}

function readContentRef(value: unknown): ContentRef | null {
  if (!isRecord(value)) return null;
  const ref = nonEmptyString(value.ref);
  return ref && isContentHash(value.contentHash)
    ? { ref, contentHash: value.contentHash }
    : null;
}

function readContentRefs(value: unknown): ContentRef[] | null {
  if (!Array.isArray(value)) return null;
  const refs = value.map(readContentRef);
  return refs.some((ref) => ref === null) ? null : (refs as ContentRef[]);
}

function readPersistenceReceipt(value: unknown): ArtifactPersistenceReceipt | null {
  if (!isRecord(value)) return null;
  const revision = readRevisionRef(value.revision);
  const persistenceWriteRef = readContentRef(value.persistenceWriteRef);
  const readbackRef = readContentRef(value.readbackRef);
  const evidenceRefs = readContentRefs(value.evidenceRefs);
  const ownerBotProfileId = value.ownerBotProfileId;
  const contentHash = value.contentHash;
  const evidenceKeys = new Set(
    evidenceRefs?.map((ref) => `${ref.ref}\u0000${ref.contentHash}`) ?? [],
  );
  if (
    value.schemaVersion !== ARTIFACT_PERSISTENCE_RECEIPT_SCHEMA_VERSION ||
    !nonEmptyString(value.receiptId) ||
    !nonEmptyString(value.projectId) ||
    (ownerBotProfileId !== null && !nonEmptyString(ownerBotProfileId)) ||
    !nonEmptyString(value.artifactId) ||
    !nonEmptyString(value.requestId) ||
    !nonEmptyString(value.runId) ||
    !nonEmptyString(value.approvalDecisionId) ||
    !nonEmptyString(value.approvalActorId) ||
    value.approvalActorKind !== "human" ||
    !revision ||
    !isContentHash(contentHash) ||
    !persistenceWriteRef ||
    !readbackRef ||
    !evidenceRefs ||
    persistenceWriteRef.contentHash !== contentHash ||
    readbackRef.contentHash !== contentHash ||
    persistenceWriteRef.ref === readbackRef.ref ||
    !evidenceKeys.has(
      `${persistenceWriteRef.ref}\u0000${persistenceWriteRef.contentHash}`,
    ) ||
    !evidenceKeys.has(`${readbackRef.ref}\u0000${readbackRef.contentHash}`) ||
    !nonEmptyString(value.persistedAt)
  ) {
    return null;
  }
  return {
    schemaVersion: ARTIFACT_PERSISTENCE_RECEIPT_SCHEMA_VERSION,
    receiptId: value.receiptId as string,
    projectId: value.projectId as string,
    ownerBotProfileId: ownerBotProfileId as string | null,
    artifactId: value.artifactId as string,
    requestId: value.requestId as string,
    runId: value.runId as string,
    approvalDecisionId: value.approvalDecisionId as string,
    approvalActorId: value.approvalActorId as string,
    approvalActorKind: "human",
    revision,
    contentHash,
    persistenceWriteRef,
    readbackRef,
    evidenceRefs,
    persistedAt: value.persistedAt as string,
  };
}

async function hashCanonicalContent(value: unknown): Promise<ContentHash> {
  const bytes = new TextEncoder().encode(stableStringify(value));
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return `sha256:${Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("")}`;
}

function artifactPersistenceWriteRef(input: {
  projectId: string;
  artifactId: string;
  requestId: string;
}): string {
  return `cogo://studio/projects/${encodeURIComponent(input.projectId)}/generated-artifacts/${encodeURIComponent(input.artifactId)}/write/${encodeURIComponent(input.requestId)}`;
}

function artifactPersistenceReadbackRef(input: {
  projectId: string;
  artifactId: string;
  revision: ArtifactRevisionRef;
}): string {
  return `cogo://studio/projects/${encodeURIComponent(input.projectId)}/generated-artifacts/${encodeURIComponent(input.artifactId)}/readback/${encodeURIComponent(input.revision.value)}`;
}

function artifactPersistenceReceiptId(input: {
  projectId: string;
  blockName: string;
  revision: ArtifactRevisionRef;
}): string {
  return `generated-artifact:${input.projectId}:${input.blockName}:${input.revision.value}`;
}

function readStoredCommit(value: unknown): StoredGeneratedArtifactCommit | null {
  if (!isRecord(value)) return null;
  const canonicalCogoJson = isRecord(value.canonicalCogoJson)
    ? value.canonicalCogoJson
    : null;
  let canonicalContentHash: ContentHash | null = null;
  try {
    canonicalContentHash = canonicalCogoJson
      ? hashCanonicalContentSync(canonicalCogoJson)
      : null;
  } catch {
    canonicalContentHash = null;
  }
  const parentRevision = readRevisionRef(value.parentRevision);
  const committedArtifactRevision = readRevisionRef(
    value.committedArtifactRevision,
  );
  const projectRevision = readProjectRevisionRef(value.projectRevision);
  const persistenceReceipt = readPersistenceReceipt(value.persistenceReceipt);
  const evidenceRefs = readContentRefs(value.evidenceRefs);
  if (
    !nonEmptyString(value.artifactId) ||
    !nonEmptyString(value.requestId) ||
    !nonEmptyString(value.runId) ||
    !nonEmptyString(value.approvalDecisionId) ||
    !nonEmptyString(value.approvalActorId) ||
    value.approvalActorKind !== "human" ||
    !isContentHash(value.contentHash) ||
    !nonEmptyString(value.blockName) ||
    !canonicalCogoJson ||
    canonicalContentHash !== value.contentHash ||
    !parentRevision ||
    !committedArtifactRevision ||
    !projectRevision ||
    !persistenceReceipt ||
    !evidenceRefs ||
    !sameRevision(committedArtifactRevision, projectRevision) ||
    !sameRevision(persistenceReceipt.revision, projectRevision) ||
    serverRevisionNumber(persistenceReceipt.projectId, projectRevision) === null ||
    persistenceReceipt.artifactId !== value.artifactId ||
    persistenceReceipt.requestId !== value.requestId ||
    persistenceReceipt.runId !== value.runId ||
    persistenceReceipt.approvalDecisionId !== value.approvalDecisionId ||
    persistenceReceipt.approvalActorId !== value.approvalActorId ||
    persistenceReceipt.approvalActorKind !== value.approvalActorKind ||
    persistenceReceipt.contentHash !== value.contentHash ||
    persistenceReceipt.receiptId !==
      artifactPersistenceReceiptId({
        projectId: persistenceReceipt.projectId,
        blockName: value.blockName as string,
        revision: persistenceReceipt.revision,
      }) ||
    persistenceReceipt.persistenceWriteRef.ref !==
      artifactPersistenceWriteRef({
        projectId: persistenceReceipt.projectId,
        artifactId: persistenceReceipt.artifactId,
        requestId: persistenceReceipt.requestId,
      }) ||
    persistenceReceipt.readbackRef.ref !==
      artifactPersistenceReadbackRef({
        projectId: persistenceReceipt.projectId,
        artifactId: persistenceReceipt.artifactId,
        revision: persistenceReceipt.revision,
      }) ||
    !sameContentRefs(persistenceReceipt.evidenceRefs, evidenceRefs) ||
    !nonEmptyString(value.recordedAt) ||
    value.recordedAt !== persistenceReceipt.persistedAt
  ) {
    return null;
  }
  return {
    artifactId: value.artifactId as string,
    requestId: value.requestId as string,
    runId: value.runId as string,
    approvalDecisionId: value.approvalDecisionId as string,
    approvalActorId: value.approvalActorId as string,
    approvalActorKind: "human",
    contentHash: value.contentHash,
    blockName: value.blockName as string,
    canonicalCogoJson: structuredClone(canonicalCogoJson),
    parentRevision: {
      authority: parentRevision.authority,
      value: parentRevision.value,
    },
    committedArtifactRevision,
    projectRevision,
    persistenceReceipt,
    evidenceRefs,
    recordedAt: value.recordedAt as string,
  };
}

function readBirthAuthority(
  value: unknown,
): GeneratedArtifactBirthAuthority | null {
  if (!isRecord(value) || !isRecord(value.botProfileParentRevision)) {
    return null;
  }
  const revision = value.botProfileParentRevision;
  if (
    !nonEmptyString(value.approvalDecisionId) ||
    !nonEmptyString(value.approvalActorId) ||
    value.approvalActorKind !== "human" ||
    revision.authority !== "studio-bot-profile-updated-at" ||
    !nonEmptyString(revision.value)
  ) {
    return null;
  }
  return {
    approvalDecisionId: value.approvalDecisionId as string,
    approvalActorId: value.approvalActorId as string,
    approvalActorKind: "human",
    botProfileParentRevision: {
      authority: "studio-bot-profile-updated-at",
      value: revision.value as string,
    },
  };
}

export function readGeneratedArtifactPersistenceSettings(
  settings: Record<string, unknown> | null,
): GeneratedArtifactPersistenceSettings | null {
  const raw = settings?.generatedArtifactPersistence;
  if (raw === undefined) return null;
  if (!isRecord(raw)) {
    throw new Error("Generated artifact server settings are malformed.");
  }
  const creationKey = raw.creationKey;
  const ownerBotProfileId = raw.ownerBotProfileId;
  const birthAuthority = readBirthAuthority(raw.birthAuthority);
  const hasBotBinding =
    creationKey !== null || ownerBotProfileId !== null || raw.birthAuthority !== null;
  if (
    raw.schemaVersion !== GENERATED_ARTIFACT_PERSISTENCE_SETTINGS_SCHEMA_VERSION ||
    (creationKey !== null && !isContentHash(creationKey)) ||
    (ownerBotProfileId !== null && !nonEmptyString(ownerBotProfileId)) ||
    (hasBotBinding
      ? !isContentHash(creationKey) ||
        !nonEmptyString(ownerBotProfileId) ||
        !birthAuthority ||
        !birthAuthority.botProfileParentRevision.value.startsWith(
          `bot-profile:${ownerBotProfileId as string}@`,
        )
      : creationKey !== null || ownerBotProfileId !== null) ||
    !Array.isArray(raw.commits)
  ) {
    throw new Error("Generated artifact server settings are malformed.");
  }
  const commits = raw.commits.map(readStoredCommit);
  if (
    commits.some((commit) => commit === null) ||
    commits.some(
      (commit) =>
        commit?.persistenceReceipt.ownerBotProfileId !== ownerBotProfileId,
    ) ||
    new Set(
      commits.map((commit) => commit?.persistenceReceipt.projectId),
    ).size > 1
  ) {
    throw new Error("Generated artifact server commit history is malformed.");
  }
  const parsedCommits = commits as StoredGeneratedArtifactCommit[];
  if (
    birthAuthority &&
    parsedCommits.length > 0 &&
    (parsedCommits[0].approvalDecisionId !==
      birthAuthority.approvalDecisionId ||
      parsedCommits[0].approvalActorId !== birthAuthority.approvalActorId ||
      parsedCommits[0].approvalActorKind !== birthAuthority.approvalActorKind)
  ) {
    throw new Error("Generated artifact Bot birth authority is disconnected.");
  }
  return {
    schemaVersion: GENERATED_ARTIFACT_PERSISTENCE_SETTINGS_SCHEMA_VERSION,
    creationKey,
    ownerBotProfileId: ownerBotProfileId as string | null,
    birthAuthority,
    commits: parsedCommits,
  };
}

const readPersistenceSettings = readGeneratedArtifactPersistenceSettings;

/**
 * Reads the platform-issued composite project+block revision. The raw
 * artifact counter and derived ref must agree; neither project row version
 * nor updated_at is accepted as a substitute for block authority.
 */
export function studioProjectServerRevisionRef(
  project: StudioProject,
): StudioProjectServerRevisionRef {
  const artifactRevision = project.artifact_revision;
  const revision = project.projectRevision;
  if (
    !Number.isSafeInteger(artifactRevision) ||
    (artifactRevision as number) < 1 ||
    revision?.authority !== STUDIO_PROJECT_SERVER_REVISION_AUTHORITY ||
    revision.value !== `${project.id}@${artifactRevision as number}`
  ) {
    throw new GeneratedArtifactProjectConflictError(
      "Exact block-bound server revision is unavailable.",
    );
  }
  return { authority: revision.authority, value: revision.value };
}

function studioProjectSettingsRevisionRef(
  project: StudioProject,
): StudioProjectSettingsRevisionRef {
  const settingsRevision = project.settings_revision;
  const revision = project.settingsRevision;
  if (
    !Number.isSafeInteger(settingsRevision) ||
    (settingsRevision as number) < 1 ||
    revision?.authority !== "studio-project-settings-server" ||
    revision.value !== `${project.id}@${settingsRevision as number}`
  ) {
    throw new GeneratedArtifactProjectConflictError(
      "Exact server settings revision is unavailable.",
    );
  }
  return { authority: revision.authority, value: revision.value };
}

function settingsRevisionNumber(
  projectId: string,
  revision: StudioProjectSettingsRevisionRef,
): number | null {
  const prefix = `${projectId}@`;
  if (
    revision.authority !== "studio-project-settings-server" ||
    !revision.value.startsWith(prefix)
  ) {
    return null;
  }
  const raw = revision.value.slice(prefix.length);
  if (!/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) && value >= 1 ? value : null;
}

function assertSettingsSuccessorRevision(
  projectId: string,
  parent: StudioProjectSettingsRevisionRef,
  successor: StudioProjectSettingsRevisionRef,
): void {
  const parentNumber = settingsRevisionNumber(projectId, parent);
  const successorNumber = settingsRevisionNumber(projectId, successor);
  if (parentNumber === null || successorNumber !== parentNumber + 1) {
    throw new GeneratedArtifactProjectConflictError(
      "The settings write did not return the current revision's exact successor.",
    );
  }
}

function sameRevision(
  left: ArtifactRevisionRef,
  right: ArtifactRevisionRef,
): boolean {
  return left.authority === right.authority && left.value === right.value;
}

function sameContentRefs(
  left: readonly ContentRef[],
  right: readonly ContentRef[],
): boolean {
  if (left.length !== right.length) return false;
  const rightKeys = new Set(
    right.map((ref) => `${ref.ref}\u0000${ref.contentHash}`),
  );
  return left.every((ref) => rightKeys.has(`${ref.ref}\u0000${ref.contentHash}`));
}

function containsContentRefs(
  observed: readonly ContentRef[],
  required: readonly ContentRef[],
): boolean {
  const observedKeys = new Set(
    observed.map((ref) => `${ref.ref}\u0000${ref.contentHash}`),
  );
  return required.every((ref) =>
    observedKeys.has(`${ref.ref}\u0000${ref.contentHash}`),
  );
}

function serverRevisionNumber(
  projectId: string,
  revision: ArtifactRevisionRef,
): number | null {
  const prefix = `${projectId}@`;
  if (
    revision.authority !== STUDIO_PROJECT_SERVER_REVISION_AUTHORITY ||
    !revision.value.startsWith(prefix)
  ) {
    return null;
  }
  const raw = revision.value.slice(prefix.length);
  if (!/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) && value >= 1 ? value : null;
}

function approvedParentRevision(
  command: GeneratedArtifactPersistenceCommand,
  projectId: string,
): StudioProjectServerRevisionRef {
  const parentRevision = command.envelope.parentRevision;
  if (!parentRevision || serverRevisionNumber(projectId, parentRevision) === null) {
    throw new GeneratedArtifactProjectConflictError(
      "The approved parent is not an exact Studio project revision.",
    );
  }
  return {
    authority: STUDIO_PROJECT_SERVER_REVISION_AUTHORITY,
    value: parentRevision.value,
  };
}

function assertSuccessorRevision(
  projectId: string,
  parentRevision: ArtifactRevisionRef,
  successorRevision: ArtifactRevisionRef,
): void {
  const parent = serverRevisionNumber(projectId, parentRevision);
  const successor = serverRevisionNumber(projectId, successorRevision);
  if (parent === null || successor !== parent + 1) {
    throw new GeneratedArtifactProjectConflictError(
      "The server-issued artifact revision is not the approved parent's exact successor.",
    );
  }
}

function assertExistingProjectParentRevision(
  command: GeneratedArtifactPersistenceCommand,
  project: StudioProject,
): StudioProjectServerRevisionRef {
  const approved = approvedParentRevision(command, project.id);
  const current = studioProjectServerRevisionRef(project);
  if (!sameRevision(approved, current)) {
    throw new GeneratedArtifactProjectConflictError();
  }
  return current;
}

function isPlatformProjectRevisionConflict(error: unknown): boolean {
  if (!isRecord(error) || error.status !== 409 || !isRecord(error.body)) {
    return false;
  }
  return error.body.error === "project-revision-conflict";
}

function artifactBlockName(command: GeneratedArtifactPersistenceCommand): string {
  const readable = command.envelope.artifactId
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 44) || "artifact";
  return `generated-${readable}-${command.envelope.contentHash.slice(7, 19)}`;
}

function readGeneratedName(proposal: GeneratedApplicationProposal): string {
  const meta = isRecord(proposal.surface.cogoJson.meta)
    ? proposal.surface.cogoJson.meta
    : null;
  return nonEmptyString(meta?.name) ?? proposal.summary;
}

function makeStoredCommit(
  command: GeneratedArtifactPersistenceCommand,
  blockName: string,
  committed: CommittedArtifactRevisionEnvelope,
): StoredGeneratedArtifactCommit {
  const parentRevision = command.envelope.parentRevision;
  if (!parentRevision) {
    throw new Error("Generated artifact persistence requires a parent revision.");
  }
  if (
    committed.persistenceReceipt.projectId.trim().length === 0 ||
    !sameRevision(committed.revision, committed.persistenceReceipt.revision) ||
    committed.persistenceReceipt.artifactId !== command.envelope.artifactId ||
    committed.persistenceReceipt.requestId !== command.envelope.requestId ||
    committed.persistenceReceipt.runId !== command.envelope.runId ||
    committed.persistenceReceipt.approvalDecisionId !==
      command.approvalDecision.decisionId ||
    committed.persistenceReceipt.approvalActorId !==
      command.approvalDecision.actorId ||
    committed.persistenceReceipt.approvalActorKind !==
      command.approvalDecision.actorKind ||
    committed.persistenceReceipt.contentHash !== command.envelope.contentHash
  ) {
    throw new Error("Generated artifact commit receipt does not match the approved command.");
  }
  const projectRevision = readProjectRevisionRef(committed.revision);
  if (!projectRevision) {
    throw new Error("Generated artifact commit requires an exact project revision.");
  }
  return {
    artifactId: command.envelope.artifactId,
    requestId: command.envelope.requestId,
    runId: command.envelope.runId,
    approvalDecisionId: command.approvalDecision.decisionId,
    approvalActorId: command.approvalDecision.actorId,
    approvalActorKind: command.approvalDecision.actorKind,
    contentHash: command.envelope.contentHash,
    blockName,
    canonicalCogoJson: structuredClone(command.proposal.surface.cogoJson),
    parentRevision: { ...parentRevision },
    committedArtifactRevision: { ...committed.revision },
    projectRevision,
    persistenceReceipt: structuredClone(committed.persistenceReceipt),
    evidenceRefs: structuredClone(committed.evidenceRefs),
    recordedAt: committed.persistenceReceipt.persistedAt,
  };
}

function mergeStoredCommit(
  current: GeneratedArtifactPersistenceSettings | null,
  commit: StoredGeneratedArtifactCommit,
  input: {
    creationKey: ContentHash | null;
    ownerBotProfileId: string | null;
  },
): GeneratedArtifactPersistenceSettings {
  if (
    current &&
    (current.creationKey !== input.creationKey ||
      current.ownerBotProfileId !== input.ownerBotProfileId)
  ) {
    throw new Error("Generated artifact persistence binding does not match the target.");
  }
  const commits = (current?.commits ?? []).filter(
    (candidate) =>
      candidate.artifactId !== commit.artifactId ||
      candidate.requestId !== commit.requestId ||
      candidate.runId !== commit.runId,
  );
  return {
    schemaVersion: GENERATED_ARTIFACT_PERSISTENCE_SETTINGS_SCHEMA_VERSION,
    creationKey: input.creationKey,
    ownerBotProfileId: input.ownerBotProfileId,
    birthAuthority: current?.birthAuthority ?? null,
    commits: [...commits, commit],
  };
}

function templateForProposal(
  proposal: GeneratedApplicationProposal,
  blockName: string,
): CreateProjectTemplate {
  return {
    manifest: {
      name: readGeneratedName(proposal),
      pages: [{ id: "home", name: "Home", route: "/", blocks: [blockName] }],
    } as CreateProjectTemplate["manifest"],
    // A new project has no prior block-bound revision to CAS against. Seed the
    // approved canonical block as part of project birth so createProject only
    // returns after the server has issued its first artifact revision. Retry
    // recovery for a pre-existing blockless birth remains fail-closed below.
    blocks: { [blockName]: structuredClone(proposal.surface.cogoJson) },
    data: {},
    flows: {},
  };
}

function persistenceSettingsPayload(
  settings: GeneratedArtifactPersistenceSettings,
): Record<string, unknown> {
  return { ...settings };
}

function sameCanonicalValue(left: unknown, right: unknown): boolean {
  if (left === undefined || right === undefined) return left === right;
  return stableStringify(left) === stableStringify(right);
}

function expectedStoredFlow(value: unknown): unknown {
  if (!isRecord(value) || "flows" in value) return value;
  const containsFlowDefinitions = Object.values(value).some(
    (candidate) =>
      isRecord(candidate) && ("steps" in candidate || "trigger" in candidate),
  );
  return containsFlowDefinitions ? { flows: value } : value;
}

function expectedLoadedState(value: unknown): unknown {
  if (!isRecord(value)) return {};
  if ("state" in value || "computed" in value || "context" in value) {
    return value;
  }
  if (isRecord(value.localState)) return { state: value.localState };
  return { state: value };
}

/**
 * Closed projection produced by project-api's buildBlockRows -> toLoadedApp
 * round trip. The flat fields are compatibility aliases emitted by that
 * reader, not a second authority. Comparing the entire projection prevents a
 * receipt-bound supplement or another runtime alias from adding unapproved
 * content while the canonical nested partitions still look unchanged.
 */
function expectedAuthenticatedBlockProjection(
  proposal: GeneratedApplicationProposal,
  blockName: string,
): Record<string, unknown> {
  const expected = proposal.surface.cogoJson;
  const expectedUi = isRecord(expected.ui) ? expected.ui : null;
  const schemaVersion =
    expectedUi?.schemaVersion ?? expected.schemaVersion ?? "3.0";
  const blockType = expectedUi?.blockType ?? expected.blockType ?? blockName;
  const root = expectedUi?.root ?? expected.root;
  const events = expected.events ?? {};
  const flow = expectedStoredFlow(expected.flow ?? {});
  const state = expectedLoadedState(expected.state ?? expected.variables ?? {});
  const data = expected.data ?? expected.datasource ?? {};
  const meta = expected.meta ?? expected.manifest ?? {};
  return {
    meta,
    ui: { schemaVersion, blockType, root },
    events,
    flow,
    state,
    data,
    schemaVersion,
    blockType,
    root,
    variables: state,
    datasource: data,
    manifest: meta,
  };
}

function assertRuntimeProjection(
  proposal: GeneratedApplicationProposal,
  readbackBlock: unknown,
  blockName: string,
): void {
  if (!isRecord(readbackBlock)) {
    throw new Error("Authenticated readback is missing the generated block.");
  }
  const expected = proposal.surface.cogoJson;
  const expectedUi = isRecord(expected.ui) ? expected.ui : null;
  const observedUi = isRecord(readbackBlock.ui) ? readbackBlock.ui : null;
  const expectedSchemaVersion =
    expectedUi?.schemaVersion ?? expected.schemaVersion ?? "3.0";
  const expectedBlockType =
    expectedUi?.blockType ?? expected.blockType ?? blockName;
  if (
    !observedUi ||
    !sameCanonicalValue(expectedUi?.root, observedUi.root) ||
    !sameCanonicalValue(expectedSchemaVersion, observedUi.schemaVersion) ||
    !sameCanonicalValue(expectedBlockType, observedUi.blockType)
  ) {
    throw new Error("Authenticated readback changed the generated UI partition.");
  }
  const partitions: Array<[string, unknown, unknown]> = [
    ["events", expected.events ?? {}, readbackBlock.events],
    ["flow", expectedStoredFlow(expected.flow ?? {}), readbackBlock.flow],
    [
      "state",
      expectedLoadedState(expected.state ?? expected.variables ?? {}),
      readbackBlock.state,
    ],
    ["data", expected.data ?? expected.datasource ?? {}, readbackBlock.data],
  ];
  for (const [partition, expectedValue, observed] of partitions) {
    if (!sameCanonicalValue(expectedValue, observed)) {
      throw new Error(
        `Authenticated readback changed the generated ${partition} partition.`,
      );
    }
  }
  if (
    expected.meta !== undefined &&
    !sameCanonicalValue(expected.meta, readbackBlock.meta)
  ) {
    throw new Error("Authenticated readback changed the generated meta partition.");
  }
  if (
    !sameCanonicalValue(
      expectedAuthenticatedBlockProjection(proposal, blockName),
      readbackBlock,
    )
  ) {
    throw new Error(
      "Authenticated readback changed the closed authenticated block projection.",
    );
  }
}

function commitMatchesCommand(
  commit: StoredGeneratedArtifactCommit,
  command: GeneratedArtifactPersistenceCommand,
): boolean {
  return (
    commit.artifactId === command.envelope.artifactId &&
    commit.requestId === command.envelope.requestId &&
    commit.runId === command.envelope.runId &&
    commit.approvalDecisionId === command.approvalDecision.decisionId &&
    commit.approvalActorId === command.approvalDecision.actorId &&
    commit.approvalActorKind === command.approvalDecision.actorKind &&
    commit.contentHash === command.envelope.contentHash &&
    command.envelope.parentRevision !== null &&
    sameRevision(commit.parentRevision, command.envelope.parentRevision) &&
    containsContentRefs(commit.evidenceRefs, command.envelope.evidenceRefs) &&
    sameCanonicalValue(
      commit.canonicalCogoJson,
      command.proposal.surface.cogoJson,
    )
  );
}

function assertNoConflictingStoredCommit(
  binding: GeneratedArtifactPersistenceSettings | null,
  command: GeneratedArtifactPersistenceCommand,
): void {
  const conflict = binding?.commits.find(
    (candidate) =>
      candidate.artifactId === command.envelope.artifactId &&
      candidate.requestId === command.envelope.requestId &&
      candidate.runId === command.envelope.runId &&
      !commitMatchesCommand(candidate, command),
  );
  if (conflict) {
    throw new Error(
      "Stored generated artifact does not match the approved artifact revision.",
    );
  }
}

function assertLoadedProjectIdentity(
  expectedProjectId: string,
  loaded: LoadedStudioProjectReadback,
): void {
  if (loaded.project.id !== expectedProjectId) {
    throw new Error("The authenticated load returned a different project id.");
  }
}

function assertSettingsWriteResponse(input: {
  projectId: string;
  settings: unknown;
  command: GeneratedArtifactPersistenceCommand;
  creationKey: ContentHash | null;
  ownerBotProfileId: string | null;
  blockName: string;
}): void {
  const binding = readPersistenceSettings(
    input.settings as Record<string, unknown> | null,
  );
  if (
    !binding ||
    binding.creationKey !== input.creationKey ||
    binding.ownerBotProfileId !== input.ownerBotProfileId
  ) {
    throw new Error("Artifact settings write response is missing its server binding.");
  }
  const commit = binding.commits.find((candidate) =>
    commitMatchesCommand(candidate, input.command),
  );
  if (
    !commit ||
    commit.blockName !== input.blockName ||
    commit.persistenceReceipt.projectId !== input.projectId ||
    commit.persistenceReceipt.ownerBotProfileId !== input.ownerBotProfileId
  ) {
    throw new Error("Artifact settings write response is missing the approved commit.");
  }
}

async function assertFreshAuthenticatedReadback(input: {
  projectId: string;
  command: GeneratedArtifactPersistenceCommand;
  blockName: string;
  creationKey: ContentHash | null;
  ownerBotProfileId: string | null;
}): Promise<LoadedStudioProjectReadback> {
  const fresh = await loadProject(input.projectId);
  assertLoadedProjectIdentity(input.projectId, fresh);
  const binding = readPersistenceSettings(fresh.settings);
  if (
    !binding ||
    binding.creationKey !== input.creationKey ||
    binding.ownerBotProfileId !== input.ownerBotProfileId
  ) {
    throw new Error("Authenticated readback is missing the artifact owner binding.");
  }
  const commit = binding.commits.find((candidate) =>
    commitMatchesCommand(candidate, input.command),
  );
  if (
    !commit ||
    commit.blockName !== input.blockName ||
    commit.persistenceReceipt.projectId !== input.projectId ||
    commit.persistenceReceipt.ownerBotProfileId !== input.ownerBotProfileId
  ) {
    throw new Error("Authenticated readback is missing the canonical artifact commit.");
  }
  const readbackHash = await hashCanonicalContent(commit.canonicalCogoJson);
  if (readbackHash !== input.command.envelope.contentHash) {
    throw new Error("Authenticated readback content hash does not match the approved artifact.");
  }
  assertRuntimeProjection(
    input.command.proposal,
    fresh.app.blocks[input.blockName],
    input.blockName,
  );
  return fresh;
}

function mergeEvidenceRefs(
  required: readonly ContentRef[],
  writeRef: ContentRef,
  readbackRef: ContentRef,
): readonly ContentRef[] {
  const refs = new Map<string, ContentRef>();
  for (const ref of [...required, writeRef, readbackRef]) {
    refs.set(`${ref.ref}\u0000${ref.contentHash}`, ref);
  }
  return [...refs.values()];
}

function committedEnvelope(input: {
  command: GeneratedArtifactPersistenceCommand;
  projectId: string;
  ownerBotProfileId: string | null;
  readbackProject: StudioProject;
  blockName: string;
  revision: StudioProjectServerRevisionRef;
  persistedAt?: string;
}): CommittedArtifactRevisionEnvelope {
  const revision = { ...input.revision };
  const writeRef: ContentRef = {
    ref: artifactPersistenceWriteRef({
      projectId: input.projectId,
      artifactId: input.command.envelope.artifactId,
      requestId: input.command.envelope.requestId,
    }),
    contentHash: input.command.envelope.contentHash,
  };
  const readbackRef: ContentRef = {
    ref: artifactPersistenceReadbackRef({
      projectId: input.projectId,
      artifactId: input.command.envelope.artifactId,
      revision,
    }),
    contentHash: input.command.envelope.contentHash,
  };
  const evidenceRefs = mergeEvidenceRefs(
    input.command.envelope.evidenceRefs,
    writeRef,
    readbackRef,
  );
  const receipt: ArtifactPersistenceReceipt = {
    schemaVersion: ARTIFACT_PERSISTENCE_RECEIPT_SCHEMA_VERSION,
    receiptId: artifactPersistenceReceiptId({
      projectId: input.projectId,
      blockName: input.blockName,
      revision,
    }),
    projectId: input.projectId,
    ownerBotProfileId: input.ownerBotProfileId,
    artifactId: input.command.envelope.artifactId,
    requestId: input.command.envelope.requestId,
    runId: input.command.envelope.runId,
    approvalDecisionId: input.command.approvalDecision.decisionId,
    approvalActorId: input.command.approvalDecision.actorId,
    approvalActorKind: input.command.approvalDecision.actorKind,
    revision,
    contentHash: input.command.envelope.contentHash,
    persistenceWriteRef: writeRef,
    readbackRef,
    evidenceRefs,
    persistedAt: input.persistedAt ?? input.readbackProject.updated_at,
  };
  return {
    ...input.command.envelope,
    revision,
    evidenceRefs,
    persistenceReceipt: receipt,
  };
}

type ExistingGeneratedArtifactTarget = Extract<
  GeneratedArtifactPersistenceCommand["envelope"]["target"],
  { kind: "existing-project" | "existing-bot-project" }
>;

function existingTargetProjectId(target: ExistingGeneratedArtifactTarget): string {
  return target.projectId;
}

function targetOwnerBotProfileId(
  target: GeneratedArtifactPersistenceCommand["envelope"]["target"],
): string | null {
  return target.kind === "existing-project" ? null : target.ownerBotProfileId;
}

function assertExistingTargetOwnerBinding(
  command: GeneratedArtifactPersistenceCommand,
  binding: GeneratedArtifactPersistenceSettings | null,
): string | null {
  const target = command.envelope.target;
  if (target.kind === "new-project") {
    throw new Error("Expected an existing generated artifact target.");
  }
  if (target.kind === "existing-project") {
    if (binding?.ownerBotProfileId) {
      throw new Error(
        "A Bot-owned project requires an explicit existing-bot-project target.",
      );
    }
    return null;
  }
  if (
    binding?.ownerBotProfileId !== target.ownerBotProfileId ||
    command.proposal.requestedBy.requestedByAgentId !== target.ownerBotProfileId
  ) {
    throw new Error(
      "Generated artifact Bot owner does not match the existing project binding.",
    );
  }
  return target.ownerBotProfileId;
}

function committedEnvelopeFromStored(
  command: GeneratedArtifactPersistenceCommand,
  commit: StoredGeneratedArtifactCommit,
  expectedProjectId: string,
): CommittedArtifactRevisionEnvelope {
  const target = command.envelope.target;
  if (
    commit.persistenceReceipt.projectId !== expectedProjectId ||
    commit.persistenceReceipt.ownerBotProfileId !== targetOwnerBotProfileId(target) ||
    !sameRevision(commit.committedArtifactRevision, commit.projectRevision) ||
    !sameRevision(commit.persistenceReceipt.revision, commit.projectRevision)
  ) {
    throw new Error("Stored generated artifact receipt does not match its target.");
  }
  return {
    ...command.envelope,
    revision: { ...commit.committedArtifactRevision },
    evidenceRefs: structuredClone(commit.evidenceRefs),
    persistenceReceipt: structuredClone(commit.persistenceReceipt),
  };
}

async function persistIntoExistingProject(
  command: GeneratedArtifactPersistenceCommand,
): Promise<CommittedArtifactRevisionEnvelope> {
  if (command.envelope.target.kind === "new-project") {
    throw new Error("Expected an existing generated artifact target.");
  }
  const projectId = existingTargetProjectId(command.envelope.target);
  const firstRead = await loadProject(projectId);
  assertLoadedProjectIdentity(projectId, firstRead);
  const blockName = artifactBlockName(command);
  const firstBinding = readPersistenceSettings(firstRead.settings);
  const ownerBotProfileId = assertExistingTargetOwnerBinding(command, firstBinding);
  assertNoConflictingStoredCommit(firstBinding, command);
  const completedCommit = firstBinding?.commits.find((candidate) =>
    commitMatchesCommand(candidate, command),
  );
  if (completedCommit) {
    await assertFreshAuthenticatedReadback({
      projectId,
      command,
      blockName: completedCommit.blockName,
      creationKey: firstBinding?.creationKey ?? null,
      ownerBotProfileId,
    });
    return committedEnvelopeFromStored(command, completedCommit, projectId);
  }
  // A complete, exact server commit is a read-only idempotent recovery and
  // needs no new parent authorization. A partial recovery is admitted only
  // at the exact successor revision of the approved parent.
  if (firstRead.app.blocks[blockName]) {
    const parentRevision = approvedParentRevision(command, projectId);
    const recoveryRevision = studioProjectServerRevisionRef(firstRead.project);
    assertSuccessorRevision(projectId, parentRevision, recoveryRevision);
    // Recover the only partial state this client write order can leave: the
    // exact content-addressed block reached the server, then the settings
    // receipt write failed. Approval is still bound by `command`; do not ask
    // the caller to rewrite the project or create a second artifact.
    assertRuntimeProjection(
      command.proposal,
      firstRead.app.blocks[blockName],
      blockName,
    );
    const recoveredCommitted = committedEnvelope({
      command,
      projectId,
      ownerBotProfileId,
      readbackProject: firstRead.project,
      blockName,
      revision: recoveryRevision,
    });
    const recoveredBinding = mergeStoredCommit(
      firstBinding,
      makeStoredCommit(command, blockName, recoveredCommitted),
      {
        creationKey: firstBinding?.creationKey ?? null,
        ownerBotProfileId,
      },
    );
    const expectedSettingsRevision = studioProjectSettingsRevisionRef(
      firstRead.project,
    );
    const settingsWrite = await patchProjectSettings(
      projectId,
      { generatedArtifactPersistence: persistenceSettingsPayload(recoveredBinding) },
      { expectedSettingsRevision },
    );
    assertSettingsSuccessorRevision(
      projectId,
      expectedSettingsRevision,
      settingsWrite.settingsRevision,
    );
    assertSettingsWriteResponse({
      projectId,
      settings: settingsWrite.settings,
      command,
      creationKey: recoveredBinding.creationKey,
      ownerBotProfileId: recoveredBinding.ownerBotProfileId,
      blockName,
    });
    const fresh = await assertFreshAuthenticatedReadback({
      projectId,
      command,
      blockName,
      creationKey: recoveredBinding.creationKey,
      ownerBotProfileId,
    });
    if (!sameRevision(studioProjectServerRevisionRef(fresh.project), recoveryRevision)) {
      throw new GeneratedArtifactProjectConflictError(
        "The project changed during exact artifact recovery.",
      );
    }
    if (
      !sameRevision(
        studioProjectSettingsRevisionRef(fresh.project),
        settingsWrite.settingsRevision,
      )
    ) {
      throw new GeneratedArtifactProjectConflictError(
        "The project settings changed during exact artifact recovery.",
      );
    }
    return recoveredCommitted;
  }
  const parentRevision = assertExistingProjectParentRevision(command, firstRead.project);
  // Narrow the race before the first mutation; the atomic Worker If-Match is
  // still the authoritative cross-client guard.
  const preWrite = await loadProject(projectId);
  assertLoadedProjectIdentity(projectId, preWrite);
  if (
    !sameRevision(
      studioProjectServerRevisionRef(firstRead.project),
      studioProjectServerRevisionRef(preWrite.project),
    )
  ) {
    throw new GeneratedArtifactProjectConflictError();
  }
  assertExistingProjectParentRevision(command, preWrite.project);

  const blocks = {
    ...preWrite.app.blocks,
    [blockName]: structuredClone(command.proposal.surface.cogoJson),
  };

  let persisted: PersistGeneratedBlocksRevisionResult;
  try {
    persisted = await persistGeneratedBlocks(
      projectId,
      blocks,
      preWrite.app.flows,
      { expectedProjectRevision: parentRevision },
    );
  } catch (error) {
    if (isPlatformProjectRevisionConflict(error)) {
      throw new GeneratedArtifactProjectConflictError();
    }
    throw error;
  }
  const issuedRevision = persisted.projectRevision;
  assertSuccessorRevision(projectId, parentRevision, issuedRevision);
  const postBlockRead = await loadProject(projectId);
  assertLoadedProjectIdentity(projectId, postBlockRead);
  if (!sameRevision(studioProjectServerRevisionRef(postBlockRead.project), issuedRevision)) {
    throw new GeneratedArtifactProjectConflictError(
      "Authenticated block readback does not match the server-issued artifact revision.",
    );
  }
  assertRuntimeProjection(
    command.proposal,
    postBlockRead.app.blocks[blockName],
    blockName,
  );
  const committed = committedEnvelope({
    command,
    projectId,
    ownerBotProfileId,
    readbackProject: postBlockRead.project,
    blockName,
    revision: issuedRevision,
  });
  const storedCommit = makeStoredCommit(command, blockName, committed);
  const postBlockBinding = readPersistenceSettings(postBlockRead.settings);
  assertExistingTargetOwnerBinding(command, postBlockBinding);
  const binding = mergeStoredCommit(postBlockBinding, storedCommit, {
    creationKey: postBlockBinding?.creationKey ?? null,
    ownerBotProfileId,
  });
  const expectedSettingsRevision = studioProjectSettingsRevisionRef(
    postBlockRead.project,
  );
  const settingsWrite = await patchProjectSettings(
    projectId,
    { generatedArtifactPersistence: persistenceSettingsPayload(binding) },
    { expectedSettingsRevision },
  );
  assertSettingsSuccessorRevision(
    projectId,
    expectedSettingsRevision,
    settingsWrite.settingsRevision,
  );
  assertSettingsWriteResponse({
    projectId,
    settings: settingsWrite.settings,
    command,
    creationKey: binding.creationKey,
    ownerBotProfileId: binding.ownerBotProfileId,
    blockName,
  });

  const fresh = await assertFreshAuthenticatedReadback({
    projectId,
    command,
    blockName,
    creationKey: binding.creationKey,
    ownerBotProfileId,
  });
  if (!sameRevision(studioProjectServerRevisionRef(fresh.project), issuedRevision)) {
    throw new GeneratedArtifactProjectConflictError(
      "Authenticated readback does not match the server-issued artifact revision.",
    );
  }
  if (
    !sameRevision(
      studioProjectSettingsRevisionRef(fresh.project),
      settingsWrite.settingsRevision,
    )
  ) {
    throw new GeneratedArtifactProjectConflictError(
      "Authenticated readback does not match the server-issued settings revision.",
    );
  }
  return committed;
}

async function botProjectCreationKey(
  command: GeneratedArtifactPersistenceCommand,
): Promise<ContentHash> {
  if (command.envelope.target.kind !== "new-project") {
    throw new Error("Expected a new-project artifact target.");
  }
  return hashCanonicalContent({
    kind: "generated-artifact-bot-project",
    ownerBotProfileId: command.envelope.target.ownerBotProfileId,
    artifactId: command.envelope.artifactId,
    requestId: command.envelope.requestId,
    runId: command.envelope.runId,
    approvalDecisionId: command.approvalDecision.decisionId,
    contentHash: command.envelope.contentHash,
  });
}

function botBirthAuthority(
  command: GeneratedArtifactPersistenceCommand,
): GeneratedArtifactBirthAuthority {
  if (command.envelope.target.kind !== "new-project") {
    throw new Error("Expected a new-project artifact target.");
  }
  const parent = command.envelope.parentRevision;
  const expectedPrefix = `bot-profile:${command.envelope.target.ownerBotProfileId}@`;
  if (
    command.approvalDecision.actorKind !== "human" ||
    !parent ||
    parent.authority !== "studio-bot-profile-updated-at" ||
    !parent.value.startsWith(expectedPrefix) ||
    parent.value.length === expectedPrefix.length
  ) {
    throw new Error(
      "Bot project birth requires the exact authenticated Bot profile parent revision.",
    );
  }
  return {
    approvalDecisionId: command.approvalDecision.decisionId,
    approvalActorId: command.approvalDecision.actorId,
    approvalActorKind: "human",
    botProfileParentRevision: {
      authority: "studio-bot-profile-updated-at",
      value: parent.value,
    },
  };
}

async function findBotProjectByCreationKey(
  creationKey: ContentHash,
  ownerBotProfileId: string,
): Promise<string | null> {
  const projects = await listProjects();
  let match: string | null = null;
  for (const project of projects) {
    // A failed project read aborts creation. Skipping it could hide the one
    // matching binding and create a duplicate on retry.
    const loaded = await loadProject(project.id);
    assertLoadedProjectIdentity(project.id, loaded);
    let binding: GeneratedArtifactPersistenceSettings | null;
    try {
      binding = readPersistenceSettings(loaded.settings);
    } catch (error) {
      const raw = loaded.settings?.generatedArtifactPersistence;
      if (isRecord(raw) && raw.creationKey === creationKey) {
        // A candidate that claims the requested deterministic creation key is
        // authoritative enough to block duplicate creation, but not to use.
        throw error;
      }
      // Settings owned by an unrelated legacy or malformed candidate cannot
      // prevent discovery of the exact creation-key project.
      continue;
    }
    if (!binding || binding.creationKey !== creationKey) continue;
    if (binding.ownerBotProfileId !== ownerBotProfileId) {
      throw new Error("Bot project creation key is bound to a different owner.");
    }
    if (match && match !== project.id) {
      throw new Error("Duplicate Bot projects already exist for the creation key.");
    }
    match = project.id;
  }
  return match;
}

async function persistIntoNewBotProject(
  command: GeneratedArtifactPersistenceCommand,
): Promise<CommittedArtifactRevisionEnvelope> {
  if (command.envelope.target.kind !== "new-project") {
    throw new Error("Expected a new-project artifact target.");
  }
  const target = command.envelope.target;
  if (command.proposal.requestedBy.requestedByAgentId !== target.ownerBotProfileId) {
    throw new Error("Generated artifact Bot owner does not match the requesting Bot profile.");
  }
  const creationKey = await botProjectCreationKey(command);
  const birthAuthority = botBirthAuthority(command);
  const blockName = artifactBlockName(command);
  const birthBinding: GeneratedArtifactPersistenceSettings = {
    schemaVersion: GENERATED_ARTIFACT_PERSISTENCE_SETTINGS_SCHEMA_VERSION,
    creationKey,
    ownerBotProfileId: target.ownerBotProfileId,
    birthAuthority,
    commits: [],
  };

  let projectId = await findBotProjectByCreationKey(
    creationKey,
    target.ownerBotProfileId,
  );
  if (!projectId) {
    try {
      projectId = await createProject(
        target.suggestedName,
        templateForProposal(command.proposal, blockName),
        {
          generatedArtifactPersistence: persistenceSettingsPayload(birthBinding),
        },
      );
    } catch (error) {
      // createProject can fail after the birth row/settings write but before
      // block persistence. Recover that exact server binding instead of
      // blindly issuing another create.
      projectId = await findBotProjectByCreationKey(
        creationKey,
        target.ownerBotProfileId,
      );
      if (!projectId) throw error;
    }
  }

  const current = await loadProject(projectId);
  assertLoadedProjectIdentity(projectId, current);
  const currentBinding = readPersistenceSettings(current.settings);
  if (
    !currentBinding ||
    currentBinding.creationKey !== creationKey ||
    currentBinding.ownerBotProfileId !== target.ownerBotProfileId ||
    stableStringify(currentBinding.birthAuthority) !==
      stableStringify(birthAuthority)
  ) {
    throw new Error("Bot-owned project readback does not match its creation binding.");
  }
  assertNoConflictingStoredCommit(currentBinding, command);
  const currentCommit = currentBinding.commits.find((candidate) =>
    commitMatchesCommand(candidate, command),
  );
  if (currentCommit) {
    await assertFreshAuthenticatedReadback({
      projectId,
      command,
      blockName: currentCommit.blockName,
      creationKey,
      ownerBotProfileId: target.ownerBotProfileId,
    });
    return committedEnvelopeFromStored(command, currentCommit, projectId);
  }

  let artifactReadback = current;
  if (!current.app.blocks[blockName]) {
    const currentRevision = studioProjectServerRevisionRef(current.project);
    let persisted: PersistGeneratedBlocksRevisionResult;
    try {
      persisted = await persistGeneratedBlocks(
        projectId,
        {
          ...current.app.blocks,
          [blockName]: structuredClone(command.proposal.surface.cogoJson),
        },
        current.app.flows,
        { expectedProjectRevision: currentRevision },
      );
    } catch (error) {
      if (isPlatformProjectRevisionConflict(error)) {
        throw new GeneratedArtifactProjectConflictError();
      }
      throw error;
    }
    assertSuccessorRevision(
      projectId,
      currentRevision,
      persisted.projectRevision,
    );
    artifactReadback = await loadProject(projectId);
    assertLoadedProjectIdentity(projectId, artifactReadback);
    if (
      !sameRevision(
        studioProjectServerRevisionRef(artifactReadback.project),
        persisted.projectRevision,
      )
    ) {
      throw new GeneratedArtifactProjectConflictError(
        "Authenticated Bot birth readback does not match the server-issued artifact revision.",
      );
    }
  }

  assertRuntimeProjection(
    command.proposal,
    artifactReadback.app.blocks[blockName],
    blockName,
  );
  const projectRevision = studioProjectServerRevisionRef(artifactReadback.project);
  const committed = committedEnvelope({
    command,
    projectId,
    ownerBotProfileId: target.ownerBotProfileId,
    readbackProject: artifactReadback.project,
    blockName,
    revision: projectRevision,
  });
  const binding = mergeStoredCommit(
    readPersistenceSettings(artifactReadback.settings),
    makeStoredCommit(command, blockName, committed),
    {
      creationKey,
      ownerBotProfileId: target.ownerBotProfileId,
    },
  );
  const expectedSettingsRevision = studioProjectSettingsRevisionRef(
    artifactReadback.project,
  );
  const settingsWrite = await patchProjectSettings(
    projectId,
    { generatedArtifactPersistence: persistenceSettingsPayload(binding) },
    { expectedSettingsRevision },
  );
  assertSettingsSuccessorRevision(
    projectId,
    expectedSettingsRevision,
    settingsWrite.settingsRevision,
  );
  assertSettingsWriteResponse({
    projectId,
    settings: settingsWrite.settings,
    command,
    creationKey,
    ownerBotProfileId: target.ownerBotProfileId,
    blockName,
  });

  const fresh = await assertFreshAuthenticatedReadback({
    projectId,
    command,
    blockName,
    creationKey,
    ownerBotProfileId: target.ownerBotProfileId,
  });
  if (!sameRevision(studioProjectServerRevisionRef(fresh.project), projectRevision)) {
    throw new GeneratedArtifactProjectConflictError(
      "The Bot project changed while its artifact receipt was recorded.",
    );
  }
  if (
    !sameRevision(
      studioProjectSettingsRevisionRef(fresh.project),
      settingsWrite.settingsRevision,
    )
  ) {
    throw new GeneratedArtifactProjectConflictError(
      "The Bot project settings changed while its artifact receipt was recorded.",
    );
  }
  return committed;
}

const inFlightBotProjectCreates = new Map<
  string,
  Promise<CommittedArtifactRevisionEnvelope>
>();

/**
 * Authenticated Studio implementation of the shared Chat/Bot persistence
 * port. It has no dependency on the selected IDE project, Zustand, toast
 * state, or localStorage.
 *
 * Bot retries are joined in-process and recovered through the deterministic
 * server settings binding. Cross-tab/process atomic uniqueness still needs a
 * Worker create-idempotency primitive; this adapter deliberately does not
 * advertise the client scan as that stronger guarantee.
 */
export class AuthenticatedStudioGeneratedArtifactPersistence
  implements GeneratedArtifactPersistencePort
{
  readonly kind = GENERATED_ARTIFACT_PERSISTENCE_PORT_KIND;

  async persistAndReadBack(
    command: GeneratedArtifactPersistenceCommand,
  ): Promise<CommittedArtifactRevisionEnvelope> {
    const computedHash = await hashCanonicalContent(
      command.proposal.surface.cogoJson,
    );
    if (computedHash !== command.envelope.contentHash) {
      throw new Error("Generated artifact content changed before persistence.");
    }
    if (command.envelope.target.kind !== "new-project") {
      return persistIntoExistingProject(command);
    }

    const creationKey = await botProjectCreationKey(command);
    const existing = inFlightBotProjectCreates.get(creationKey);
    if (existing) return existing;
    const operation = persistIntoNewBotProject(command).finally(() => {
      if (inFlightBotProjectCreates.get(creationKey) === operation) {
        inFlightBotProjectCreates.delete(creationKey);
      }
    });
    inFlightBotProjectCreates.set(creationKey, operation);
    return operation;
  }
}

export const authenticatedStudioGeneratedArtifactPersistence =
  new AuthenticatedStudioGeneratedArtifactPersistence();
